import { z } from 'zod';

/**
 * ページサイズの選択肢（数値リテラルunion）
 */
export const PageSize = z.union([z.literal(30), z.literal(50), z.literal(100), z.literal(1000)]);
export type PageSize = z.infer<typeof PageSize>;

/** ページサイズの選択肢を配列として取得 */
export const PAGE_SIZE_OPTIONS = [30, 50, 100, 1000] as const;

// デフォルト検索パラメータ
export const DEFAULT_SEARCH = {
  page: 1,
  pageSize: PAGE_SIZE_OPTIONS[0],
} as const;

/**
 * 共通ページネーションスキーマ
 *
 * 全ての一覧画面で使用する標準的なページネーションパラメータを定義
 * - page: ページ番号（1始まり）
 * - page_size: 1ページあたりの件数
 *
 *  navigate時にsearchが必須になるためdefaultの後にoptionalを指定する
 */
export const paginationSchema = z.object({
  page: z.number().int().min(1).default(DEFAULT_SEARCH.page).optional(),
  page_size: PageSize.default(DEFAULT_SEARCH.pageSize).optional(),
});

type WithPaginationValidateSearchProps<T extends z.ZodTypeAny> = {
  record: Record<string, unknown>;
  usePaginationSchema?: boolean;
} & (
  | {
      usePaginationSchema: true;
      withPaginationSchema?: T;
      customSchema?: never;
      defaultValues?: Partial<z.infer<T>> | undefined;
    }
  | {
      usePaginationSchema: false;
      customSchema: T;
      withPaginationSchema?: never;
      defaultValues?: Partial<z.infer<T>> | undefined;
    }
  | {
      usePaginationSchema?: never;
      withPaginationSchema?: never;
      customSchema?: never;
      defaultValues?: never;
    }
);

/**
 * 検索パラメータのバリデーション
 *
 * @template T - Zodスキーマの型（デフォルト: paginationSchema）
 * @param record - バリデーション対象のレコード
 * @param withPaginationSchema - カスタムスキーマ（省略時はpaginationSchemaを使用）
 * @param defaultValues - バリデーション失敗時のデフォルト値
 * @returns バリデーション済みのデータ
 */
export function routeValidateQuerySearch<T extends z.ZodTypeAny = typeof paginationSchema>({
  record,
  withPaginationSchema,
  defaultValues,
  usePaginationSchema = false,
  customSchema,
}: WithPaginationValidateSearchProps<T>): z.infer<T> {
  const schema = usePaginationSchema
    ? ((withPaginationSchema ?? paginationSchema) as T)
    : (customSchema as T);
  const result = schema.safeParse(record);
  if (!result.success) {
    return schema.parse({
      ...(usePaginationSchema ? DEFAULT_SEARCH : {}),
      ...defaultValues,
    }) as z.infer<T>;
  }
  return result.data as z.infer<T>;
}
